import { useCallback, useMemo, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { EstatisticasChecklistServico } from "../../domain/models/estatisticas_checklist_servico";
import { ChecklistServicoDetalhe } from "../../domain/models/checklist_servico_detalhe";
import { initialChecklistServicoObrasItem, useChecklistServicoObrasItemFilters } from "../hooks/ChecklistServico_obrasItem/filters";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { ChartsAxisData } from "@mui/x-charts";
import { ItemChecklistServicoStatistic } from "../../domain/models/item_checklist_servico_statistic";

import dayjs from "dayjs";

/**
 * useChecklistServicoObrasItemViewModel
 */

export const useChecklistServicoObrasItemViewModel = () => {
  const {
    obras: {
      getEstatisticasChecklistServicoUseCase,
      getChecklistServicoByIdUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idChecklistServico, setIdChecklistServico] = useState<number>();
  const [checklistServico, setChecklistServico] = useState<ChecklistServicoDetalhe>();
  const [estatisticasChecklistServico, setEstatisticasChecklistServico] = useState<EstatisticasChecklistServico>();
  const [selectedItemChecklistServico, setSelectedItemChecklistServico] = useState<ItemChecklistServicoStatistic | null>(null);

  const [maxValue, setMaxValue] = useState<number>(0);

  const [itemChecklistServicoStatisticModal, setItemChecklistServicoStatisticModal] = useState(false);

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();

  const [currentTab, setCurrentTab] = useState(1);

  const filters = useChecklistServicoObrasItemFilters();

  const activeFields = useMemo(() => {
    if (estatisticasChecklistServico === undefined) {
      return [];
    }
    if (estatisticasChecklistServico.fields === null) {
      return [];
    }
    return Object
      .entries(estatisticasChecklistServico.fields)
      .filter(([_, value]) => filters.status === 2 || value.delete === (filters.status === 0));
  }, [
    filters,
    estatisticasChecklistServico,
  ]);

  const filteredEstatisticasChecklistServico = useMemo((): EstatisticasChecklistServico | null => {
    if (estatisticasChecklistServico === undefined) return null;

    const fieldArrayMap = activeFields.map(x => x[0]);

    const recalculatedData = estatisticasChecklistServico.data.filter(item => {

      // Descricao
      if (filters.resumoDescricao.length > 0) {
        if (!item.areaDescricao.toLowerCase().includes(filters.resumoDescricao.toLowerCase())) {
          return false;
        }
      }

      const hasValidDataAbertura = item.dataInspecao && item.dataInspecao.length > 0 && dayjs(item.dataInspecao).isValid();
      const hasValidDataEncerramento = item.dataEncerramento && item.dataEncerramento.length > 0 && dayjs(item.dataEncerramento).isValid();

      // Data Abertura
      const hasAbertInic = filters.dataAberturaInicio && dayjs(filters.dataAberturaInicio).isValid();
      const hasAbertTerm = filters.dataAberturaTermino && dayjs(filters.dataAberturaTermino).isValid();

      if ((hasAbertInic || hasAbertTerm) && !hasValidDataAbertura) return false;

      if (hasAbertInic && hasAbertTerm) {
        const dataInspecao = dayjs(item.dataInspecao).toDate();
        if (dataInspecao.getTime() < filters.dataAberturaInicio!.getTime() ||
          dataInspecao.getTime() > filters.dataAberturaTermino!.getTime()) {
          return false;
        }
      }
      else if (hasAbertInic) {
        const dataInspecao = dayjs(item.dataInspecao).toDate();
        if (dataInspecao.getTime() < filters.dataAberturaInicio!.getTime()) {
          return false;
        }
      }
      else if (hasAbertTerm) {
        const dataInspecao = dayjs(item.dataInspecao).toDate();
        if (dataInspecao.getTime() > filters.dataAberturaTermino!.getTime()) {
          return false;
        }
      }

      // Data Encerramento
      const hasEncerInic = filters.dataEncerramentoInicio && dayjs(filters.dataEncerramentoInicio).isValid();
      const hasEncerTerm = filters.dataEncerramentoTermino && dayjs(filters.dataEncerramentoTermino).isValid();

      if ((hasEncerInic || hasEncerTerm) && !hasValidDataEncerramento) return false;

      if (hasEncerInic && hasEncerTerm) {
        const dataEncerramento = dayjs(item.dataEncerramento).toDate();
        if (dataEncerramento.getTime() < filters.dataEncerramentoInicio!.getTime() ||
          dataEncerramento.getTime() > filters.dataEncerramentoTermino!.getTime()) {
          return false;
        }
      }
      else if (hasEncerInic) {
        const dataEncerramento = dayjs(item.dataEncerramento).toDate();
        if (dataEncerramento.getTime() < filters.dataEncerramentoInicio!.getTime()) {
          return false;
        }
      }
      else if (hasEncerTerm) {
        const dataEncerramento = dayjs(item.dataEncerramento).toDate();
        if (dataEncerramento.getTime() > filters.dataEncerramentoTermino!.getTime()) {
          return false;
        }
      }

      // CentroCusto
      if (filters.centroCustos !== null && filters.centroCustos.length > 0) {
        if (!filters.centroCustos.some(x => x === item.idCentroCusto)) {
          return false;
        }
      }
      return true;
    });

    const recalculatedStatistics = recalculatedData.reduce((acc: any, item) => {
      const initial = {
        aprovados: 0,
        reprovados: 0,
        retrabalhos: 0,
        naoSeAplica: 0,
      };

      Object.entries(item.values).forEach(([campo, valor]) => {
        if (!fieldArrayMap.some(x => x === campo)) return;

        if (!acc[campo]) {
          acc[campo] = { ...initial };
        }

        if (!valor) return;

        const normalizedValue = valor.replace(/[()]/g, "");

        if (normalizedValue === "A") {
          acc[campo].aprovados++;
        }
        else if (normalizedValue === "N") {
          acc[campo].naoSeAplica++;
        }
        else if (normalizedValue === "X") {
          acc[campo].reprovados++;
        }
        else if (normalizedValue === "R") {
          acc[campo].retrabalhos++;
        }
        else if (normalizedValue === "RA") {
          acc[campo].aprovados++;
          acc[campo].retrabalhos++;
        }
        else if (normalizedValue === "RX") {
          acc[campo].reprovados++;
          acc[campo].retrabalhos++;
        }
      });

      return acc;
    }, {});

    return {
      ...estatisticasChecklistServico,
      data: recalculatedData,
      statistics: recalculatedStatistics,
    };
  }, [
    activeFields,
    estatisticasChecklistServico,
    filters,
  ]);

  const chartWidth = useMemo(() => {
    if (estatisticasChecklistServico === undefined) {
      return null;
    }
    const size = activeFields.length;
    if (size <= 15) {
      return null;
    }
    return size * 150;
  }, [
    activeFields,
    estatisticasChecklistServico,
  ]);

  const xAxis = useMemo((): any => {
    if (estatisticasChecklistServico === undefined) {
      return null;
    }
    if (estatisticasChecklistServico.fields === null) {
      return null;
    }
    return [{
      scaleType: "band",
      tickLabelStyle: { whiteSpace: "nowrap" },
      data: activeFields.map(([_, value]) => {
        let descricao = "";
        if (value.resumoItem && value.resumoItem.length > 0) {
          descricao = value.resumoItem;
        }
        else if (value.descricao && value.descricao.length > 0) {
          descricao = value.descricao;
        }
        const label = descricao.length > 30
          ? descricao.substring(0, 30) + '...'
          : descricao;
        return `${value.idItemChecklistServico} - ${label}`;
      }),
    }];
  }, [
    activeFields,
    estatisticasChecklistServico,
  ]);

  const series = useMemo((): any => {
    const dataAprovados: number[] = [];
    const dataRetrabalhos: number[] = [];
    const dataReprovados: number[] = [];
    const dataNaoSeAplica: number[] = [];

    if (estatisticasChecklistServico && estatisticasChecklistServico.data && estatisticasChecklistServico.fields) {
      const fieldArrayMap = activeFields.map(x => x[0]);

      const size = fieldArrayMap.length;

      for (let i = 0; i < size; i++) {
        dataAprovados.push(0);
        dataRetrabalhos.push(0);
        dataReprovados.push(0);
        dataNaoSeAplica.push(0);
      }

      estatisticasChecklistServico.data
        .filter((item) => {

          // Centro Custo
          if (filters.centroCustos !== null && filters.centroCustos.length > 0) {
            if (!filters.centroCustos.some(x => x === item.idCentroCusto)) {
              return false;
            }
          }

          const hasValidDataAbertura = item.dataInspecao && item.dataInspecao.length > 0 && dayjs(item.dataInspecao).isValid();
          const hasValidDataEncerramento = item.dataEncerramento && item.dataEncerramento.length > 0 && dayjs(item.dataEncerramento).isValid();

          // Data Abertura
          const hasAbertInic = filters.dataAberturaInicio && dayjs(filters.dataAberturaInicio).isValid();
          const hasAbertTerm = filters.dataAberturaTermino && dayjs(filters.dataAberturaTermino).isValid();

          if ((hasAbertInic || hasAbertTerm) && !hasValidDataAbertura) return false;

          if (hasAbertInic && hasAbertTerm) {
            const dataInspecao = dayjs(item.dataInspecao).toDate();
            if (dataInspecao.getTime() < filters.dataAberturaInicio!.getTime() ||
              dataInspecao.getTime() > filters.dataAberturaTermino!.getTime()) {
              return false;
            }
          }
          else if (hasAbertInic) {
            const dataInspecao = dayjs(item.dataInspecao).toDate();
            if (dataInspecao.getTime() < filters.dataAberturaInicio!.getTime()) {
              return false;
            }
          }
          else if (hasAbertTerm) {
            const dataInspecao = dayjs(item.dataInspecao).toDate();
            if (dataInspecao.getTime() > filters.dataAberturaTermino!.getTime()) {
              return false;
            }
          }

          // Data Encerramento
          const hasEncerInic = filters.dataEncerramentoInicio && dayjs(filters.dataEncerramentoInicio).isValid();
          const hasEncerTerm = filters.dataEncerramentoTermino && dayjs(filters.dataEncerramentoTermino).isValid();

          if ((hasEncerInic || hasEncerTerm) && !hasValidDataEncerramento) return false;

          if (hasEncerInic && hasEncerTerm) {
            const dataEncerramento = dayjs(item.dataEncerramento).toDate();
            if (dataEncerramento.getTime() < filters.dataEncerramentoInicio!.getTime() ||
              dataEncerramento.getTime() > filters.dataEncerramentoTermino!.getTime()) {
              return false;
            }
          }
          else if (hasEncerInic) {
            const dataEncerramento = dayjs(item.dataEncerramento).toDate();
            if (dataEncerramento.getTime() < filters.dataEncerramentoInicio!.getTime()) {
              return false;
            }
          }
          else if (hasEncerTerm) {
            const dataEncerramento = dayjs(item.dataEncerramento).toDate();
            if (dataEncerramento.getTime() > filters.dataEncerramentoTermino!.getTime()) {
              return false;
            }
          }
          return true;
        })
        .forEach(item => {
          Object.entries(item.values).forEach(([key, value]) => {
            const normalizedValue = value.replace(/[()]/g, "");
            const fieldIndex = fieldArrayMap.indexOf(key);

            if (fieldIndex >= 0) {
              switch (normalizedValue) {
                case 'A':
                  dataAprovados[fieldIndex] += 1;
                  break;

                case 'R':
                  dataRetrabalhos[fieldIndex] += 1;
                  break;

                case 'RA':
                  dataAprovados[fieldIndex] += 1;
                  dataRetrabalhos[fieldIndex] += 1;
                  break;

                case 'RX':
                  dataReprovados[fieldIndex] += 1;
                  dataRetrabalhos[fieldIndex] += 1;
                  break;

                case 'X':
                  dataReprovados[fieldIndex] += 1;
                  break;

                case 'N':
                  dataNaoSeAplica[fieldIndex] += 1;
                  break;
              }
            }
          });
        });

      const allValues = [...dataAprovados, ...dataReprovados, ...dataRetrabalhos, ...dataNaoSeAplica];
      const maxValue = Math.max(...allValues);

      setMaxValue(maxValue + Math.ceil(maxValue * 0.065));
    }

    return [
      {
        label: 'Aprovados',
        id: 'data-a',
        data: dataAprovados,
        color: '#26C281',
        barLabel: 'value',
        minBarSize: 5,
        barLabelPlacement: 'outside',
      },
      {
        label: 'Reprovados',
        id: 'data-x',
        data: dataReprovados,
        color: '#EF4836',
        barLabel: 'value',
        minBarSize: 5,
        barLabelPlacement: 'outside',
      },
      {
        label: 'Retrabalhados',
        id: 'data-r',
        data: dataRetrabalhos,
        color: '#FFB848',
        barLabel: 'value',
        minBarSize: 5,
        barLabelPlacement: 'outside'
      },
      {
        label: 'Não se aplica',
        id: 'data-n',
        data: dataNaoSeAplica,
        color: '#ffff00',
        barLabel: 'value',
        minBarSize: 5,
        barLabelPlacement: 'outside'
      },
    ];
  }, [
    filters,
    activeFields,
    estatisticasChecklistServico,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadChecklistServico = useCallback(async (id?: number) => {
    try {
      const filteredIdChecklistServico = id ?? idChecklistServico;

      const result = await getChecklistServicoByIdUseCase.getChecklistServicoById(
        filteredIdChecklistServico,
      );

      setChecklistServico(result);
    } catch (e: unknown) {
      setChecklistServico(undefined);
      throw new Error('Falha ao buscar checklist de serviço!');
    }
  }, [
    idChecklistServico,
    getChecklistServicoByIdUseCase,
  ]);

  const loadEstatisticasChecklistServico = useCallback(async (id?: number) => {
    try {
      const filteredIdChecklistServico = id ?? idChecklistServico;

      const response = await getEstatisticasChecklistServicoUseCase
        .getEstatisticasChecklistServico(
          filteredIdChecklistServico,
        );

      setEstatisticasChecklistServico(response);
    } catch (e: unknown) {
      setEstatisticasChecklistServico(undefined);
      throw new Error('Falha ao buscar estatisticas de checklist de serviço!');
    }
  }, [
    idChecklistServico,
    getEstatisticasChecklistServicoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationIdChecklistServico = params.get('id');
      const locIdChecklistServico = locationIdChecklistServico ?? undefined;

      if (!locIdChecklistServico || locIdChecklistServico.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de serviço!");
        return;
      }
      setIdChecklistServico(Number(locIdChecklistServico));

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadChecklistServico(Number(locIdChecklistServico));

      await loadEstatisticasChecklistServico(Number(locIdChecklistServico));

      useChecklistServicoObrasItemFilters.setState({ ...initialChecklistServicoObrasItem });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/ChecklistServico_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idChecklistServico) {
      try {
        setLoading(true);

        await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

        await loadChecklistServico(idChecklistServico);

        await loadEstatisticasChecklistServico(idChecklistServico);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idChecklistServico,
    loadCentroCustoDropDown,
    loadChecklistServico,
    loadEstatisticasChecklistServico,
    pushErrorNotification,
  ]);

  const handleResumoDescricaoChange = useCallback((descricao: string) => {
    useChecklistServicoObrasItemFilters.setState({ resumoDescricao: descricao });
  }, [
  ]);

  const handleStatusChange = useCallback((status: number) => {
    useChecklistServicoObrasItemFilters.setState({ status: status });
  }, [
  ]);

  const handleGraficoCentroCustosChange = useCallback((centroCustos: number[] | null) => {
    useChecklistServicoObrasItemFilters.setState({ centroCustos: centroCustos });
  }, [
  ]);

  const handleDataAberturaChange = useCallback((dataInicio: Date | null, dataTermino: Date | null) => {
    useChecklistServicoObrasItemFilters.setState({ dataAberturaInicio: dataInicio, dataAberturaTermino: dataTermino });
  }, [
  ]);

  const handleDataEncerramentoChange = useCallback((dataInicio: Date | null, dataTermino: Date | null) => {
    useChecklistServicoObrasItemFilters.setState({ dataEncerramentoInicio: dataInicio, dataEncerramentoTermino: dataTermino });
  }, [
  ]);

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  }, []);

  const handleClearFilters = useCallback(() => {
    useChecklistServicoObrasItemFilters.setState({ ...initialChecklistServicoObrasItem });
  }, [
  ]);

  const handleItemChecklistClick = useCallback((item: ChartsAxisData | null) => {
    if (item !== null) {
      const itemChecklistServico = activeFields[item.dataIndex];
      if (itemChecklistServico) {
        const itemChecklistServicoValue = itemChecklistServico[1];
        if (itemChecklistServicoValue) {
          setSelectedItemChecklistServico({
            idItemChecklistServico: itemChecklistServicoValue.idItemChecklistServico,
            ordem: itemChecklistServicoValue.ordem,
            descricao: itemChecklistServicoValue.descricao,
            resumoItem: itemChecklistServicoValue.resumoItem,
            delete: itemChecklistServicoValue.delete,
            aprovados: item.seriesValues['data-a'] ?? 0,
            reprovados: item.seriesValues['data-x'] ?? 0,
            retrabalhos: item.seriesValues['data-r'] ?? 0,
            naoSeAplica: item.seriesValues['data-n'] ?? 0,
          });

          requestAnimationFrame(() => setItemChecklistServicoStatisticModal(true));
        }
      }
    }
  }, [
    activeFields,
  ]);

  const handleItemChecklistServicoModalOpenState = useCallback((openState: boolean) => () => {
    setItemChecklistServicoStatisticModal(openState);
  }, [
  ]);

  return {
    loading,
    errorMessage,
    idChecklistServico,
    checklistServico,
    estatisticasChecklistServico: filteredEstatisticasChecklistServico,
    selectedItemChecklistServico: selectedItemChecklistServico ?? null,
    maxValue,
    itemChecklistServicoStatisticModal,
    centroCustoDropdown,
    currentTab,
    filters,
    activeFields,
    chartWidth,
    xAxis,
    series,
    handleBack,
    handleReload,
    handleResumoDescricaoChange,
    handleStatusChange,
    handleGraficoCentroCustosChange,
    handleDataAberturaChange,
    handleDataEncerramentoChange,
    handleTabChange,
    handleClearFilters,
    handleItemChecklistClick,
    handleItemChecklistServicoModalOpenState,
  };
}